/*!
  Pioneer WFRIA Timer v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Timer"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Timer") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Timer");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { useCountdown } from "../common/hooks";
/**
 * Timer is a utility which allows an application to initiate, and interact with,
 * a countdown timer by altering its state. Features include the option to set the
 * countdown time, display interval, receive formatted time, and more.
 */

function Timer(_ref) {
  let {
    format,
    interval,
    onComplete,
    render,
    threshold,
    timeLimit,
    timerState
  } = _ref;
  const [result, {
    startCountdown,
    stopCountdown,
    resetCountdown
  }] = useCountdown({
    format,
    interval,
    onComplete,
    threshold,
    timeLimit
  });
  const {
    count,
    formattedCount
  } = result;
  const currentTimerStateRef = useRef();
  useEffect(() => {
    /* istanbul ignore else */
    if (timerState === Timer.TIMER_STATE.STOPPED) {
      stopCountdown();
    } else if (timerState === Timer.TIMER_STATE.PAUSED) {
      stopCountdown();
    } else if (timerState === Timer.TIMER_STATE.RESET) {
      resetCountdown();
    } else if (timerState === Timer.TIMER_STATE.RUNNING) {
      if (currentTimerStateRef.current !== Timer.TIMER_STATE.PAUSED) {
        resetCountdown();
      }

      startCountdown();
    }

    currentTimerStateRef.current = timerState; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerState]);
  return render({
    formattedTime: formattedCount,
    time: {
      minutes: Math.floor(count / 60),
      seconds: Math.round(count % 60)
    },
    timeInMilliseconds: count * 1000
  });
}
/**
 * An Object containing the supported values for `props.timerState`.
 *
 */


Timer.TIMER_STATE = {
  /**
   * Used to pause / hold the timer. The timer can be later resumed by setting the state to
   * `Timer.TIMER_STATE.RUNNING`.
   */
  PAUSED: "PAUSED",

  /**
   * Used to reset the timer, which will cancel the timer and render the countdown from the beginning.
   */
  RESET: "RESET",

  /**
   * Used to start the timer from the beginning or from the last paused countdown.
   */
  RUNNING: "RUNNING",

  /**
   * Used to stop / cancel the timer. The timer cannot be resumed.
   */
  STOPPED: "STOPPED"
};
Timer.propTypes = {
  /**
   * Two choices: 'm:s' or 's'.<br/>
   * - m:s yields 1 minute 6 seconds.<br/>
   * - s   yields 66 seconds.
   */
  format: PropTypes.oneOf(["m:s", "s"]),

  /**
   * The interval at which the timer will display updates. For example, an interval of 10 seconds means
   * that the countdown display will update every ten seconds. Therefore, a timeLimit of 60 seconds
   * will update at 50 seconds, 40 seconds, 30 seconds, etc.
   */
  interval: PropTypes.number,

  /**
   * Callback that fires once the Timer has completed.
   */
  onComplete: PropTypes.func,

  /**
   * Provides an object, via the render props pattern, which grants access to `time` in
   * the form of an object containing seconds and minutes properties, `formattedTime`,
   * which returns a string formatted according to values added in the `format` prop,
   * and `timeInMilliseconds` which returns the time in milliseconds.
   */
  render: PropTypes.func,

  /**
   * The number of seconds remaining at which point the countdown interval happens every second. While
   * there is a default threshold of 5 seconds, the reality is that the timer logic will begin to countdown
   * each second based on threshold and interval settings.  For example, a threshold of 10 and an interval
   * of 10 would never display a reasonable countdown.  In this case, the countdown will decrement by 1
   * over the course of 10 seconds.
   */
  threshold: PropTypes.number,

  /**
   * The initial time from which the timer will count down.
   */
  timeLimit: PropTypes.number,

  /**
   * Represents the state of the Timer.
   */
  timerState: PropTypes.oneOf([Timer.TIMER_STATE.RUNNING, Timer.TIMER_STATE.PAUSED, Timer.TIMER_STATE.STOPPED, Timer.TIMER_STATE.RESET])
};
Timer.defaultProps = {
  format: "s",
  interval: 10,
  onComplete: null,
  render: null,
  threshold: 5,
  timeLimit: 60,
  timerState: Timer.TIMER_STATE.STOPPED
};
Timer.displayName = "Timer";
export default Timer;